import { useMemo, useState } from 'react';
import { Spinner } from '@wordpress/components';
import {
	ManagementProvider,
	useManagement,
} from '../context/ManagementContext';
import { BookingCard } from './BookingCard';
import { BookingStoreProvider } from '../../fields/contexts/BookingStoreContext';
import { createBookingStore } from '../../fields/store/createBookingStore';
import type { Booking } from '../../typings/common/booking';

interface Props {}

interface BookingGroup {
	service: Booking;
	resources: Booking[];
}

/**
 * A booking card with a booking store of its own.
 *
 * The reschedule picker keeps its selection, service, resources and excluded
 * booking in the store. An entry can hold bookings from several Booking fields,
 * each rescheduled from its own card, so a shared store would let one open
 * picker overwrite another's settings and selection.
 */
function IsolatedBookingCard({ service, resources }: BookingGroup) {
	const [store] = useState(() =>
		createBookingStore(`booking-management-${service.id}`)
	);

	return (
		<BookingStoreProvider store={store}>
			<BookingCard booking={service} resources={resources} />
		</BookingStoreProvider>
	);
}

function BookingManagement({}: Props) {
	const localizedData = window.gpbBookingManagement;

	if (!localizedData) {
		return (
			<div className="gpb-manage-error">
				Error: Booking data unavailable.
			</div>
		);
	}

	const {
		key: managementKey,
		bookings: initialBookings,
		cancellationReason = 'off',
	} = localizedData;

	return (
		<ManagementProvider
			managementKey={managementKey}
			bookings={initialBookings}
			cancellationReason={cancellationReason}
		>
			<BookingManagementInner />
		</ManagementProvider>
	);
}

function BookingManagementInner() {
	const { bookings, isLoading, error } = useManagement();

	const groupedBookings = useMemo<BookingGroup[]>(() => {
		// Group resources by parent booking ID
		const resourceGroups = new Map<number, Booking[]>();

		bookings.forEach((booking) => {
			if (booking.objectType === 'resource' && booking.parentBookingId) {
				const parentId = booking.parentBookingId;
				if (!resourceGroups.has(parentId)) {
					resourceGroups.set(parentId, []);
				}
				resourceGroups.get(parentId)?.push(booking);
			}
		});

		// Create service bookings from resource groups
		const groups: BookingGroup[] = [];
		resourceGroups.forEach((resources, parentId) => {
			if (resources.length > 0) {
				const firstResource = resources[0];
				// Create a service booking from the resource data
				const serviceBooking: Booking = {
					...firstResource,
					id: parentId,
					objectType: 'service',
					objectTitle: `${firstResource.serviceName || firstResource.objectTitle}`,
					parentBookingId: null,
				};
				groups.push({
					service: serviceBooking,
					resources,
				});
			}
		});

		// Handle standalone service bookings
		bookings.forEach((booking) => {
			if (
				!(booking.objectType === 'resource' && booking.parentBookingId)
			) {
				groups.push({
					service: {
						...booking,
						objectType: 'service',
					},
					resources: [],
				});
			}
		});

		return groups;
	}, [bookings]);

	if (isLoading) {
		return (
			<div className="gpb-manage-loading">
				<Spinner />
			</div>
		);
	}

	if (error) {
		return <div className="gpb-manage-error">{error}</div>;
	}

	return (
		<div className="gpb-manage">
			{groupedBookings.map(({ service, resources }) => (
				<IsolatedBookingCard
					key={service.id}
					service={service}
					resources={resources}
				/>
			))}
		</div>
	);
}

export default BookingManagement;
